import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  MenuItem,
  InputAdornment,
  LinearProgress,
  IconButton,
  Snackbar,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";
import StoreIcon from "@mui/icons-material/Store";
import LineStyleIcon from "@mui/icons-material/LineStyle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CategoryIcon from "@mui/icons-material/Category";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import InventoryIcon from "@mui/icons-material/Inventory";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useApiRegisterUser } from "../../hooks/api/useApiRegisterUser";
import {
  RequestCheckDuplicateEmailAddressRequest,
  RequestCreateMstUsersRequest,
} from "../../services/OpenAPI";
import QRcordImage from "../../assets/QRcord.png";
import { useApiCheckDuplicateMailAddress } from "../../hooks/api/useApiCheckDuplicateMailAddress";
import { Permission } from "../../constants/roles";

const SignUp = () => {
  const [isDetail, setIsDetail] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const { registerUser, isLoading } = useApiRegisterUser();
  const { checkDuplicateMailAddress } = useApiCheckDuplicateMailAddress();

  // 基本情報（SignUp）
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // 詳細情報（SignUpDetail）
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lineName, setLineName] = useState("");
  const [shopName, setShopName] = useState("");
  const [introducer, setIntroducer] = useState("");
  const [driverName, setDriverName] = useState("");
  const [category, setCategory] = useState("");
  const [averageMonthlyShipment, setAverageMonthlyShipment] = useState("");
  const [hasLargeItems, setHasLargeItems] = useState("-");

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success"
  );
  const handleCloseToast = () => setToastOpen(false);

  const isPasswordValid = password.length >= 8;
  const isValidLength = password.length >= 8;
  const isPasswordMatch = password === confirmPassword;

  const handleNext = async () => {
    if (!isPasswordValid || !isPasswordMatch) return;
    const req: RequestCheckDuplicateEmailAddressRequest = {
      mailAddress: email,
    };
    const result = await checkDuplicateMailAddress(req);
    if (result === null) {
      // 通信・サーバーエラー（重複とは区別する）
      setToastMessage(
        "通信エラーが発生しました。時間をおいて再度お試しください。"
      );
      setToastSeverity("error");
      setToastOpen(true);
    } else if (result) {
      setIsDetail(true);
    } else {
      setToastMessage("登録されているメールアドレスです。");
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  const checkInput = () => {
    return (
      lastName.trim() === "" ||
      firstName.trim() === "" ||
      email.trim() === "" ||
      lineName.trim() === "" ||
      shopName.trim() === "" ||
      driverName.trim() === "" ||
      category.trim() === "" ||
      averageMonthlyShipment.trim() === "" ||
      hasLargeItems.trim() === ""
    );
  };
  const handleRegister = async () => {
    const user: RequestCreateMstUsersRequest = {
      tenantId: 1,
      baseId: 0,
      createdUserId: 0,
      lastName: lastName,
      firstName: firstName,
      mail: email,
      password: password,
      permissionId: Permission.General,
      lineName: lineName,
      shopName: shopName,
      introducer: introducer,
      driverName: driverName,
      category: category,
      shippingNum: Number(averageMonthlyShipment),
      largeProduct: hasLargeItems === "〇" ? true : false,
    };
    const response = await registerUser(user);
    if (response) {
      setIsComplete(true);
    } else {
      // 失敗時に何も表示しないと、登録できたと誤認されるため必ず通知する
      setToastMessage(
        "登録に失敗しました。時間をおいて再度お試しください。"
      );
      setToastSeverity("error");
      setToastOpen(true);
    }
  };

  if (isComplete) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" gutterBottom>
          ご登録いただきありがとうございます。
          <br />
          QRコードからLINE友達追加の後、
          <br />
          「納品代行利用」というメッセージを送信して下さい。
          <br />
          承認後にログイン可能となります。
        </Typography>
        <Box
          component="img"
          src={QRcordImage}
          alt="LINE QRコード"
          sx={{ width: 360, height: 360, my: 2 }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => (window.location.href = "/login")}
        >
          ログイン画面へ戻る
        </Button>
      </Box>
    );
  }
  return (
    <Box
      sx={{
        width: "100vw", // 全画面幅
        height: "100vh", // 全画面高さ
        overflow: "auto", // 外側でスクロールを制御
      }}
    >
      {/* <Box
        sx={{
          maxWidth: "600px",
          mx: "auto",
          mt: 4,
          height: "100vh",
          // overflowY: "auto",
          paddingX: 2,
          position: "relative",
        }}
      > */}
      <Box className="scrollable-page" maxWidth="800px" mx="auto" mt={4}>
        <Typography variant="h4" align="center" gutterBottom>
          Sign Up
        </Typography>

        {!isDetail ? (
          <>
            <TextField
              label="メールアドレス（今後のログインID）"
              placeholder="Email Address"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ marginTop: 8 }}
            />
            <TextField
              label="パスワード（ログインに使用します）"
              placeholder="Password"
              type={showPassword ? "text" : "password"}
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box pl={5}>
              <Typography
                color={isPasswordValid ? "green" : "error"}
                fontSize="14px"
              >
                ● 8文字以上
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min((password.length / 8) * 100, 100)}
                sx={{
                  height: 6,
                  borderRadius: 4,
                  mb: 3,
                  width: "50%",
                  backgroundColor: "#ddd",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: isValidLength ? "#2EE59D" : "#e53935",
                  },
                }}
              />
            </Box>
            <TextField
              label="パスワードを再入力"
              placeholder="Confirm Password"
              type={showConfirmPassword ? "text" : "password"}
              fullWidth
              margin="normal"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                    >
                      {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              color="primary"
              // fullWidth
              sx={{ marginTop: 4, width: "30%", mx: "auto", display: "block" }}
              // sx={{ width: 200, height: 50, fontSize: "1rem" }}
              onClick={handleNext}
              disabled={!isPasswordValid || !isPasswordMatch}
            >
              次へ
            </Button>
          </>
        ) : (
          <>
            <TextField
              label="性"
              placeholder="ラクロジ"
              fullWidth
              margin="normal"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="名"
              placeholder="太郎"
              fullWidth
              margin="normal"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="メールアドレス"
              fullWidth
              margin="normal"
              value={email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
              disabled
            />
            <TextField
              label="LINE名"
              placeholder="ラクロジ太郎"
              fullWidth
              margin="normal"
              value={lineName}
              onChange={(e) => setLineName(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LineStyleIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="ショップ名"
              placeholder="ラクロジプロ"
              fullWidth
              margin="normal"
              value={shopName}
              onChange={(e) => setShopName(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <StoreIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="ご紹介者の氏名"
              placeholder="ラクロジ花子（いない場合は未入力）"
              fullWidth
              margin="normal"
              value={introducer}
              onChange={(e) => setIntroducer(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="配送予定名を入力してください"
              placeholder="ラクロジ太郎、ラクロジ花子（すべて記入してください）"
              fullWidth
              margin="normal"
              value={driverName}
              onChange={(e) => setDriverName(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocalShippingIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="取り扱いジャンル"
              placeholder="日用品、おもちゃ etc..."
              fullWidth
              margin="normal"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CategoryIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="月間平均出荷数"
              placeholder="10（数字のみ）"
              fullWidth
              margin="normal"
              value={averageMonthlyShipment}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (/^\d*$/.test(inputValue)) {
                  setAverageMonthlyShipment(inputValue);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ShoppingCartIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="大型商品の有無を選択してください"
              select
              fullWidth
              margin="normal"
              value={hasLargeItems}
              onChange={(e) => setHasLargeItems(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <InventoryIcon />
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="〇">〇</MenuItem>
              <MenuItem value="－">－</MenuItem>
            </TextField>
            <Button
              variant="contained"
              color="primary"
              // fullWidth
              sx={{ mt: 3, mb: 3, width: "30%", mx: "auto", display: "block" }}
              onClick={handleRegister}
              disabled={checkInput() || isLoading === true}
            >
              登録
            </Button>
            <Typography>　</Typography>
          </>
        )}
        <Snackbar
          open={toastOpen}
          autoHideDuration={4000}
          onClose={handleCloseToast}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <MuiAlert
            onClose={handleCloseToast}
            severity={toastSeverity}
            sx={{ width: "100%" }}
            elevation={6}
            variant="filled"
          >
            <span dangerouslySetInnerHTML={{ __html: toastMessage }} />
          </MuiAlert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default SignUp;
